import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import TestSelectors from '@racemap/utilities/consts/test_selectors';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { getEventTypeLabel } from '@racemap/utilities/functions/events';
import { isNotEmptyString } from '@racemap/utilities/functions/utils';
import { Typography } from 'antd';
import type { Immutable } from 'immer';
import { type FC, useState } from 'react';
import { Tristate } from 'src/components/utils/tristateTask';
import { useIsPhone, useIsTinyPhone } from 'src/lib/customHooks';
import { useSetInterval } from 'src/lib/customHooks/useSetInterval';
import type { CurrentEvent } from '../../../store/events/events_reducers';
import { useStore } from '../../../store/reducers';
import { IconFail, IconOk, IconSpinner } from '../../BasicComponents/Icons';
import { RelativeTime } from '../../BasicComponents/TimeDuration/RelativeTime';
import { EventInfoBadges, PaymentStateBadge } from '../../EventInfoBadges';
import { PriceField } from './PriceField';

const { Text } = Typography;

interface Props {
  event: Immutable<CurrentEvent>;
}

export const EventMetaInfos: FC<Props> = ({ event }) => {
  const isPhone = useIsPhone();
  const typeLabel = getEventTypeLabel(event.type);

  return (
    <Container>
      <FirstLine className={TestSelectors.admin.eventEditor.titleBar.identityFirstLine.toString()}>
        <EventTitle event={event} />
        <EventInfoBadges
          event={event}
          compact
          statusIconOnly={isPhone}
          withPaymentStateBadge={!isPhone}
        />
      </FirstLine>
      <SecondLine
        className={TestSelectors.admin.eventEditor.titleBar.identitySecondLine.toString()}
      >
        {isPhone && <PaymentStateBadge event={event} />}
        <PriceField event={event} inline />
        {!isPhone && isNotEmptyString(typeLabel) && <TypeLabel>{typeLabel}</TypeLabel>}
        <UpdatedAtLabel event={event} />
      </SecondLine>
    </Container>
  );
};

const EventTitle: FC<Props> = ({ event }) => {
  const { updateEvent } = useStore((s) => s.events.actions);
  const hasName = isNotEmptyString(event.name);

  const rename = (name: string) => {
    if (!isNotEmptyString(name.trim()) || name === event.name) return;
    updateEvent(event.id, [{ key: 'name', newValue: name.trim() }]);
  };

  return (
    <Title>
      <Text
        title={hasName ? event.name : undefined}
        italic={!hasName}
        strong={hasName}
        editable={{
          triggerType: ['text'],
          onChange: rename,
          tooltip: 'Click to rename',
          // Otherwise the placeholder itself is offered up as the name to edit.
          text: hasName ? event.name : '',
        }}
      >
        {hasName ? event.name : 'Untitled'}
      </Text>
    </Title>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const FirstLine = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  overflow: hidden;

  /* only the title may shrink */
  & > * + * {
    flex: none;
  }
`;

const Title = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 15px;
  line-height: 19px;
  padding: 0 4px;
  margin-left: -4px;
  border: 1px solid transparent;
  border-radius: 3.5px;

  &:hover {
    border-color: #ced4da;
    background: #fafafa;
  }
`;

const TypeLabel = styled.span`
  font-size: 13px;
`;

const SecondLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  font-size: 12px;
  line-height: 15px;
  white-space: nowrap;
  color: ${RacemapColors.DarkGray};

  & > * + *::before {
    content: '·';
    margin-right: 6px;
  }
`;

const UpdatedAtLabel: FC<{ event: Immutable<CurrentEvent> }> = ({ event }) => {
  const isPhone = useIsPhone();
  const isTinyPhone = useIsTinyPhone();
  const [count, setCount] = useState(0);
  useSetInterval(() => {
    setCount(count + 1);
  }, OneSecondInMillis);
  const savingState = useStore((s) => s.events.savingState);

  return (
    <div>
      {savingState === Tristate.SPINNING ? (
        <>
          <IconSpinner /> saving
        </>
      ) : savingState === Tristate.FAILURE ? (
        <>
          <IconFail /> Save failed
        </>
      ) : (
        // A phone row cannot spare the word; the check mark says it.
        <span title="Saved">
          <IconOk /> {isTinyPhone ? '' : 'saved '}
          <RelativeTime
            time={event.updatedAt}
            size={isPhone ? 'narrow' : 'long'}
            fontSize="inherit"
            color="inherit"
          />
        </span>
      )}
    </div>
  );
};
